import { type Ref, unref, useContext, useStore } from "@nuxtjs/composition-api";

import { getWishlistsStore } from "@/store/wishlists";
import { handleErrorStatusCode, handleServiceError } from "@/utils/api";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useWishlists
 * @desc     The Composable is used to fetch the user's wishlist and
 *           store it in the wishlist store. Takes in two params, the
 *           wishlist feed and whether to overwrite the existing wishlist
 *           in the store.
 *
 * @summary  Fetches the user's wishlist and stores it in the wishlist store.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param wishlistFeed - The wishlist feed to fetch.
 * @param overwrite - Whether to overwrite the existing wishlist in the store.
 *
 * @returns - A Promise that resolves when the wishlist has been fetched and stored.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export async function useWishlists(wishlistFeed: string | Ref<string>, overwrite: boolean | Ref<boolean>) {
  const ctx = useContext();
  const wishlistStore = getWishlistsStore(useStore());

  const wishlistFeedValue = unref(wishlistFeed);
  const overwriteValue = unref(overwrite);

  if (ctx.$auth.loggedIn && !wishlistStore.hasFeedBeenFetched(wishlistFeedValue)) {
    try {
      await wishlistStore.fetchMyWishes({ overwrite: overwriteValue });
    } catch (error) {
      return handleServiceError({
        context: ctx,
        error: {
          status: handleErrorStatusCode(error),
          errorCode: "likes-not-found",
          developerMessage: "Error fetching likes",
        },
        originalError: error,
      });
    }
  }
}
